import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Standalone v2 Modal (no dependency on legacy ComposedModal)
// Props:
// - isOpen: boolean
// - onClose: () => void
// - title: string
// - size: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto'
// - footer?: ReactNode
// - headerActions?: ReactNode
// - closeOnBackdropClick?: boolean (default true)
// - closeOnEscape?: boolean (default true)
// - children: ReactNode
export default function Modal({
  isOpen,
  onClose,
  size = 'auto',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  children,
}) {
  const handleKeyDown = useCallback(
    (event) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose?.();
      }
    },
    [closeOnEscape, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow || 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleBackdropClick = (event) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const sizeClasses = (() => {
    switch (size) {
      case 'auto':
        return '';
      case 'sm':
        return 'w-full sm:max-w-[50vw] sm:h-[50vh]';
      case 'md':
        return 'sm:max-w-[60vw] sm:h-[50vh]';
      case 'lg':
        return 'sm:max-w-[70vw] sm:h-[50vh]';
      case 'xl':
        return 'sm:max-w-[80vw] sm:h-[50vh]';
      case 'full':
        return 'sm:max-w-none sm:w-screen sm:h-screen sm:rounded-none';
      default:
        return 'sm:max-w-[90vw] lg:max-w-[80vw]';
    }
  })();

  const isAuto = size === 'auto';

  const containerClasses = [
    'flex flex-col',
    'text-[#121212] dark:text-[#D0D0D0]',
    'w-[100vw]',
    'sm:w-[90vw] md:w-[80vw] xl:w-[50vw]',
    isAuto ? '' : 'h-full',
    isAuto ? '' : 'min-h-[30vh]',
    'max-h-full sm:max-h-[95vh] md:max-h-[80vh] lg:max-h-[85vh]',
    sizeClasses,
  ].join(' ');

  const modalContent = (
    <div
      className="fixed left-0 right-0 top-0 bottom-0 z-50 bg-black/75 flex justify-center items-center w-full h-full"
      onClick={handleBackdropClick}
    >
      <div className={containerClasses} role="dialog" aria-modal="true">
        <div className={`flex min-h-0 w-full justify-center ${isAuto ? 'items-center' : 'h-full items-stretch'}`}>
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}